import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import RefEntryModal from "./RefEntryModal";

/**
 * FunctionsList — CRUD reference page for the `functions` table.
 * FUN_ADM/FUN_MOD-or-SUPER writes (2026-08-20). Layout mirrors CodesList / PayeesList.
 */
const PAGE_SIZE = 25;

export default function FunctionsList({ canWrite }) {
  const [fns, setFns] = useState(null);
  const [err, setErr] = useState("");
  const [idFilter, setIdFilter] = useState("");
  const [descFilter, setDescFilter] = useState("");
  const [modal, setModal] = useState(null);   // { mode, row }
  const [page, setPage] = useState(0);

  const load = () => {
    setFns(null);
    api.listFunctions()
      .then((rows) => setFns(rows || []))
      .catch((e) => setErr(e?.response?.data?.detail || e.message || "Failed to load functions"));
  };
  useEffect(() => { load(); }, []);

  const rows = useMemo(() => {
    if (!fns) return null;
    const i = idFilter.trim().toLowerCase();
    const d = descFilter.trim().toLowerCase();
    if (!i && !d) return fns;
    return fns.filter((r) => {
      const id = String(r.function_id ?? "").toLowerCase();
      const desc = String(r.function_desc ?? "").toLowerCase();
      return (!i || id.includes(i)) && (!d || desc.includes(d));
    });
  }, [fns, idFilter, descFilter]);

  const anyFilter = idFilter || descFilter;
  const clearFilters = () => { setIdFilter(""); setDescFilter(""); };
  useEffect(() => { setPage(0); }, [idFilter, descFilter]);
  const total = rows?.length ?? 0;
  const pagedRows = useMemo(
    () => (rows ? rows.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE) : null),
    [rows, page]
  );
  const filterInputStyle = {
    width: "100%", padding: "4px 8px", fontSize: 13,
    border: "1px solid #555", borderRadius: 4, fontWeight: "normal",
  };

  const fields = [
    { name: "function_id", label: "Function", maxLength: 12, required: true,
      pk: true, hint: "max 12 chars" },
    { name: "function_desc", label: "Description", maxLength: 100,
      hint: "max 100 chars", gridSpan: 2 },
  ];

  const handleSave = async (payload) => {
    if (modal.mode === "create") {
      await api.createFunction(payload);
    } else {
      await api.updateFunction(modal.row.function_id, { function_desc: payload.function_desc });
    }
    setModal(null); load();
  };
  const handleDelete = async () => {
    await api.deleteFunction(modal.row.function_id);
    setModal(null); load();
  };

  return (
    <section data-testid="functions-section">
      <p className="section-title">Functions</p>
      <div className="card list-card txn-card" data-testid="functions-card">
        <div className="list-header txn-header">
          <div className="list-header-left txn-header-left">
            <span className="list-count count" data-testid="functions-count">
              {fns
                ? `${(rows || []).length} of ${fns.length} function${fns.length === 1 ? "" : "s"}`
                : "Loading…"}
            </span>
            {anyFilter && (
              <button className="btn-ghost btn-sm" onClick={clearFilters}
                      data-testid="functions-flt-clear">Clear filters</button>
            )}
          </div>
          {canWrite && (
            <div className="list-header-right txn-header-right">
              <button className="btn-primary"
                      data-testid="functions-new-btn"
                      onClick={() => setModal({ mode: "create", row: {} })}
              >+ New function</button>
            </div>
          )}
        </div>

        <div className="list-scroll txn-scroll" data-testid="functions-scroll">
          {err && <div className="form-error" style={{ margin: "14px 18px 0" }}>{err}</div>}
          {!rows ? (
            <div className="loading-row"><span className="spinner" /> Loading functions…</div>
          ) : (
            <table className="list-table list-table-compact txn-table txn-table-compact" data-testid="functions-table">
              <thead>
                <tr className="list-head-labels txn-head-labels">
                  <th style={{ width: "22%" }}>Function</th>
                  <th>Description</th>
                </tr>
                <tr className="list-head-filters txn-head-filters">
                  <th>
                    <input data-testid="functions-filter-id" type="text"
                      placeholder="Filter function" value={idFilter}
                      onChange={(e) => setIdFilter(e.target.value)}
                      style={filterInputStyle} />
                  </th>
                  <th>
                    <input data-testid="functions-filter-desc" type="text"
                      placeholder="Filter description" value={descFilter}
                      onChange={(e) => setDescFilter(e.target.value)}
                      style={filterInputStyle} />
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={2} style={{ textAlign: "center", padding: "18px",
                                             color: "var(--muted, #666)" }}
                        data-testid="functions-empty">
                      No functions match the current filters.
                    </td>
                  </tr>
                ) : (
                  pagedRows.map((r) => (
                    <tr key={r.function_id}
                        data-testid={`functions-row-${r.function_id}`}
                        onClick={() => setModal({ mode: "edit", row: r })}
                        style={{ cursor: "pointer" }}>
                      <td style={{ fontFamily: "var(--font-mono, monospace)" }}>{r.function_id}</td>
                      <td>{r.function_desc}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>

        {total > PAGE_SIZE && (
          <div className="list-pager" data-testid="functions-pager">
            <span className="list-pager-info" data-testid="functions-pager-info">
              Showing {page * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE + PAGE_SIZE, total)} of {total}
            </span>
            <div className="list-pager-btns">
              <button className="btn-ghost" data-testid="functions-prev"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}>‹ Prev</button>
              <button className="btn-ghost" data-testid="functions-next"
                disabled={(page + 1) * PAGE_SIZE >= total}
                onClick={() => setPage((p) => p + 1)}>Next ›</button>
            </div>
          </div>
        )}
      </div>

      {modal && (
        <RefEntryModal
          testIdPrefix="functions-modal"
          mode={modal.mode}
          title={modal.mode === "create" ? "New function" : `Edit function ${modal.row.function_id}`}
          fields={fields}
          initial={modal.row}
          canWrite={canWrite}
          onSave={handleSave}
          onDelete={modal.mode === "edit" ? handleDelete : undefined}
          onClose={() => setModal(null)}
        />
      )}
    </section>
  );
}
